// src/services/offline/syncManager.service.ts
import { indexedDBService } from './indexedDB.service';
import { syncQueueService } from './syncQueue.service';
import { workloadService } from '../workload.service';
import { addressService } from '../address.service';
import { surveyService } from '../survey.service';
import { surveyResponseService } from '../surveyResponse.service';
import { SyncQueueItem, SyncStatus } from '../../types/offline.types';

type SyncEventType = 'sync-start' | 'sync-progress' | 'sync-complete' | 'sync-error' | 'conflict';

interface SyncEvent {
  type: SyncEventType;
  data?: any;
}

type SyncEventListener = (event: SyncEvent) => void;

class SyncManagerService {
  private isSyncing = false;
  private syncInterval: NodeJS.Timeout | null = null;
  private listeners: Set<SyncEventListener> = new Set();
  private autoSyncEnabled = true;
  private autoSyncIntervalMs = 30000; // 30 seconds

  /**
   * Subscribe to sync events
   */
  subscribe(listener: SyncEventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Emit an event to all listeners
   */
  private emit(event: SyncEvent): void {
    this.listeners.forEach((listener) => listener(event));
  }

  /**
   * Start auto-sync
   */
  startAutoSync(): void {
    if (this.syncInterval) return;

    this.autoSyncEnabled = true;
    this.syncInterval = setInterval(() => {
      if (navigator.onLine && this.autoSyncEnabled) {
        this.sync();
      }
    }, this.autoSyncIntervalMs);
  }

  /**
   * Stop auto-sync
   */
  stopAutoSync(): void {
    this.autoSyncEnabled = false;
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Check if currently syncing
   */
  isSyncInProgress(): boolean {
    return this.isSyncing;
  }

  /**
   * Main sync function
   */
  async sync(): Promise<{ success: boolean; synced: number; failed: number }> {
    if (this.isSyncing) {
      return { success: false, synced: 0, failed: 0 };
    }

    if (!navigator.onLine) {
      return { success: false, synced: 0, failed: 0 };
    }

    this.isSyncing = true;
    this.emit({ type: 'sync-start' });

    let synced = 0;
    let failed = 0;

    try {
      // Get pending items from queue
      const pendingItems = await syncQueueService.getPending();

      for (const item of pendingItems) {
        try {
          await syncQueueService.markAsSyncing(item.id);
          await this.syncItem(item);
          await syncQueueService.markAsSynced(item.id);
          synced++;

          this.emit({
            type: 'sync-progress',
            data: { synced, total: pendingItems.length, current: item },
          });
        } catch (error) {
          console.error('Sync error for item:', item.id, error);
          await syncQueueService.markAsFailed(
            item.id,
            error instanceof Error ? error.message : 'Unknown error'
          );
          failed++;
        }
      }

      this.emit({
        type: 'sync-complete',
        data: { synced, failed },
      });

      return { success: failed === 0, synced, failed };
    } catch (error) {
      console.error('Sync error:', error);
      this.emit({
        type: 'sync-error',
        data: { error },
      });
      return { success: false, synced, failed };
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Sync a single queue item
   */
  private async syncItem(item: SyncQueueItem): Promise<void> {
    switch (item.collection) {
      case 'workloads':
        await this.syncWorkload(item);
        break;
      case 'addresses':
        await this.syncAddress(item);
        break;
      case 'surveys':
        await this.syncSurvey(item);
        break;
      case 'surveyResponses':
        await this.syncSurveyResponse(item);
        break;
      default:
        throw new Error(`Unknown collection: ${item.collection}`);
    }
  }

  /**
   * Sync a workload
   */
  private async syncWorkload(item: SyncQueueItem): Promise<void> {
    const { operation, data, documentId } = item;

    switch (operation) {
      case 'create':
        await workloadService.create(data);
        break;
      case 'update':
        await workloadService.update(documentId, data);
        break;
      case 'delete':
        await workloadService.delete(documentId, data.deletedBy);
        break;
    }

    // Update local sync status
    await indexedDBService.saveWorkload(data, 'synced');
  }

  /**
   * Sync an address
   */
  private async syncAddress(item: SyncQueueItem): Promise<void> {
    const { operation, data, documentId } = item;

    switch (operation) {
      case 'create':
        await addressService.create(data.workloadId, data);
        break;
      case 'update':
        // Check if this is a status update (from offline canvassing)
        if (data.status && data.lastVisitedBy) {
          await addressService.updateStatus(
            data.workloadId,
            data.addressId || documentId,
            data.status,
            data.lastVisitedBy,
            data.lastVisitedByName || 'Unknown',
            data.location
          );
        } else {
          await addressService.update(data.workloadId, documentId, data);
        }
        break;
      case 'delete':
        await addressService.delete(data.workloadId, documentId);
        break;
    }

    // Update local sync status
    await indexedDBService.saveAddress(data, 'synced');
  }

  /**
   * Sync a survey
   */
  private async syncSurvey(item: SyncQueueItem): Promise<void> {
    const { operation, data, documentId } = item;

    switch (operation) {
      case 'create':
        await surveyService.create(data);
        break;
      case 'update':
        await surveyService.update(documentId, data);
        break;
      case 'delete':
        await surveyService.delete(documentId);
        break;
    }

    // Update local sync status
    await indexedDBService.saveSurvey(data, 'synced');
  }

  /**
   * Sync a survey response
   */
  private async syncSurveyResponse(item: SyncQueueItem): Promise<void> {
    const { operation, data, documentId } = item;

    switch (operation) {
      case 'create':
        // Handle offline survey response submissions
        // Extract submitter info from queued data
        const responseData = {
          surveyId: data.surveyId,
          surveyVersion: data.surveyVersion,
          workloadId: data.workloadId,
          workloadName: data.workloadName,
          addressId: data.addressId,
          projectId: data.projectId,
          responses: data.responses,
          startedAt: data.startedAt ? new Date(data.startedAt) : new Date(),
          discontinued: data.discontinued || false,
          residentName: data.residentName,
          residentPhone: data.residentPhone,
          residentEmail: data.residentEmail,
          addressStreet: data.addressStreet,
        };
        await surveyResponseService.create(
          responseData,
          data.submittedBy || 'unknown',
          data.submittedByName || 'Unknown User',
          data.location
        );
        break;
      case 'update':
        await surveyResponseService.update(documentId, data);
        break;
      case 'delete':
        await surveyResponseService.delete(documentId);
        break;
    }

    // Update local sync status
    await indexedDBService.saveSurveyResponse(data, 'synced');
  }

  /**
   * Download all data from server for offline use
   */
  async downloadForOffline(projectId: string): Promise<void> {
    this.emit({ type: 'sync-start' });

    try {
      // Download workloads
      const workloads = await workloadService.getProjectWorkloads(projectId);
      await indexedDBService.saveWorkloads(workloads);

      // Download addresses for each workload
      for (const workload of workloads) {
        const addresses = await addressService.getWorkloadAddresses(workload.id);
        await indexedDBService.saveAddresses(addresses);
      }

      // Download surveys
      const surveys = await surveyService.getProjectSurveys(projectId);
      await indexedDBService.saveSurveys(surveys);

      // Save last sync timestamp
      await indexedDBService.setMeta('lastFullSync', Date.now());

      this.emit({ type: 'sync-complete', data: { downloaded: true } });
    } catch (error) {
      console.error('Download error:', error);
      this.emit({ type: 'sync-error', data: { error } });
      throw error;
    }
  }

  /**
   * Get sync status
   */
  async getStatus(): Promise<{
    isSyncing: boolean;
    pendingCount: number;
    lastSync: number | null;
    conflicts: number;
  }> {
    const stats = await syncQueueService.getStats();
    const lastSync = await indexedDBService.getMeta<number>('lastSync');

    return {
      isSyncing: this.isSyncing,
      pendingCount: stats.pending,
      lastSync: lastSync || null,
      conflicts: stats.conflict,
    };
  }

  /**
   * Force sync (ignore if already syncing)
   */
  async forceSync(): Promise<void> {
    if (this.isSyncing) {
      // Wait for current sync to complete
      return new Promise((resolve) => {
        const unsubscribe = this.subscribe((event) => {
          if (event.type === 'sync-complete' || event.type === 'sync-error') {
            unsubscribe();
            resolve();
          }
        });
      });
    }

    await this.sync();
  }

  /**
   * Clear all offline data
   */
  async clearOfflineData(): Promise<void> {
    await indexedDBService.clearAll();
  }
}

export const syncManagerService = new SyncManagerService();
